/**
 * Timer display theme tokens — shared by:
 * - apps/web/src/pages/admin/TimerPreviewPage.tsx (editor preview)
 * - apps/web/src/pages/display/TVScreen.tsx (live TV display at /tv/:slug)
 *
 * Adding a new theme: add a key to TimerTheme + THEMES + isValidTheme().
 */

export type TimerTheme = 'custom' | 'pokerfans';

export interface ThemeTokens {
  bgColor: string;
  bgImage?: string;
  bgPattern?: string;
  bgSize?: string;
  widgetBg: string;
  widgetBorder: string;
  widgetBorderActive: string;
  primaryColor: string;
  accentBg: string;
  accentBreakBg: string;
  timerColor: string;
  timerBreakColor: string;
  timerWarningColor: string;
  blindsBg: string;
  blindsValueColor: string;
  blindsAnteColor: string;
  blindsAnteValueColor: string;
  blindsLabelColor: string;
  nextInfoColor: string;
  statLabelColor: string;
  statValueColor: string;
  prizePoolValueColor: string;
  sideInfoColor: string;
  marqueeBg: string;
  marqueeColor: string;
  fontFamily: string;
  letterSpacing: string;
  /** Logo widget text alignment within the cell. Default 'left'. */
  logoAlign?: 'left' | 'right';
  /** Pixel gap between widget cells. Default 2 (legacy). 0 = touching widget content. Grid lines are rendered separately. */
  gridGapPx?: number;
  /** Width (px) of grid lines drawn by the shared overlay. Default 1. */
  borderWidthPx?: number;
  /** Override colour for the logo widget's brand label (siteName). Falls back to primaryColor. */
  brandLabelColor?: string;
  /** Per-theme default fontSizes — merged on top of DEFAULT_FONT_SIZES when a fresh slot is created or Reset. Keys override individually. */
  defaultFontSizes?: Record<string, number>;
  /** Vertical gap (px) between stat card label and value. Default 4 (Tailwind mb-1). */
  statGapPx?: number;
  /** Inner padding (px) of stat card. Default 8 (Tailwind p-2). */
  statPadPx?: number;
}

export const THEMES: Record<TimerTheme, ThemeTokens> = {
  custom: {
    bgColor: 'var(--brand-night)',
    widgetBg: 'var(--brand-night)',
    widgetBorder: 'var(--border-subtle)',
    widgetBorderActive: 'var(--white)',
    primaryColor: 'var(--white)',
    accentBg: 'var(--brand-blue)',
    accentBreakBg: 'var(--brand-blue)',
    timerColor: 'var(--white)',
    timerBreakColor: 'var(--white)',
    timerWarningColor: 'var(--brand-red)',
    blindsBg: 'var(--brand-night)',
    blindsValueColor: 'var(--white)',
    blindsAnteColor: 'var(--blue-100)',
    blindsAnteValueColor: 'var(--white)',
    blindsLabelColor: 'var(--blue-100)',
    nextInfoColor: 'var(--blue-100)',
    statLabelColor: 'var(--blue-100)',
    statValueColor: 'var(--white)',
    prizePoolValueColor: 'var(--white)',
    sideInfoColor: 'var(--white)',
    marqueeBg: 'var(--brand-night)',
    marqueeColor: 'var(--white)',
    fontFamily: 'var(--font-mono)',
    letterSpacing: 'normal',
  },
  // Pure-black variant inspired by Pokerfans timer reference. Logo brand right-aligned, LEVEL no box, marquee white.
  pokerfans: {
    bgColor: '#000000',
    widgetBg: 'rgba(15, 18, 39, 0.9)',
    widgetBorder: 'rgba(232, 242, 255, 0.86)',
    widgetBorderActive: '#fff069',
    primaryColor: '#fff069',
    accentBg: 'transparent',
    accentBreakBg: 'rgba(20, 24, 52, 0.85)',
    timerColor: '#ffffff',
    timerBreakColor: '#69eaff',
    timerWarningColor: '#ff344f',
    blindsBg: 'linear-gradient(180deg, rgba(20, 24, 52, 0.72), rgba(12, 15, 33, 0.94))',
    blindsValueColor: '#ffffff',
    blindsAnteColor: '#fff069',
    blindsAnteValueColor: '#ffffff',
    blindsLabelColor: '#fff069',
    nextInfoColor: '#d8e6f5',
    statLabelColor: '#fff069',
    statValueColor: '#ffffff',
    prizePoolValueColor: '#ffffff',
    sideInfoColor: '#ffffff',
    marqueeBg: 'rgba(8, 12, 27, 0.96)',
    marqueeColor: '#ffffff',
    fontFamily: 'Arial, "Noto Sans TC", "PingFang TC", sans-serif',
    letterSpacing: 'normal',
    logoAlign: 'right',
    gridGapPx: 0,
    borderWidthPx: 2,
    brandLabelColor: '#ffffff',
    statGapPx: 2,
    statPadPx: 4,
    defaultFontSizes: {
      timer: 360,
      blinds: 100,
      ante: 40,
      level: 60,
      logoName: 60,
      logoLabel: 60,
      clock: 50,
      statLabel: 25,
      entries: 60,
      avgStack: 60,
      prizePool: 60,
      nextLevel: 60,
      rebuyAddon: 60,
      elapsed: 40,
      sideInfo: 30,
      marquee: 50,
      nextInfo: 40,
      blindsLabel: 25,
      lateReg: 25,
    },
  },
};

export function isValidTheme(v: unknown): v is TimerTheme {
  return v === 'custom' || v === 'pokerfans';
}
